import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import PropertySettingsUpdateMutation from 'src/mutations/PropertySettingsUpdateMutation';
import FlashesStore from 'src/stores/FlashesStore';
import getFullAddressString from 'src/util/address';

import PropertySettingsForm from './PropertySettingsForm';
import PropertySettingsMeterTariff from './PropertySettingsMeterTariff';
import PropertySettingsUsers from './PropertySettingsUsers';

const MUTATION_NAME = 'propertySettingsUpdate';

const PropertySettingsFragment = graphql`
  fragment PropertySettings_property on Property @throwOnFieldError {
    uuid
    title
    address {
      line1
      line2
      city
      state
      postcode
    }
    ...PropertySettingsForm_property
    ...PropertySettingsMeterTariff_property
    ...PropertySettingsUsers_property
  }
`;

/**
 * PropertySettings functional component for editing a property's settings.
 * @param {object} props - Component props.
 * @param {object} props.property - Property fragment reference.
 * @param {object} props.router - Found router used for post-update navigation.
 * @returns {React.ReactElement} PropertySettings component.
 */
function PropertySettings({ property = null, router }) {
  const intl = useIntl();
  const [processing, setProcessing] = useState(false);
  const propertyData = useFragment(PropertySettingsFragment, property);

  if (!propertyData) {
    return <Loading />;
  }

  const { address, uuid, title } = propertyData;

  const updatedSuccessMessage = intl.formatMessage({ id: 'property.property_settings.response_message.success', defaultMessage: 'Updated property settings.' });

  const handlePropertySettingsUpdateSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: { errors: [error] },
      }, MUTATION_NAME));
      return;
    }

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      updatedSuccessMessage,
      MUTATION_NAME,
    );

    router.push(`/properties/${uuid}`);
  };

  const handlePropertySettingsUpdateFailure = (error) => {
    setProcessing(false);
    FlashesStore.flash(FlashesStore.ERROR, error, MUTATION_NAME);
  };

  const handlePropertySettingsUpdate = (propertyUuid, input) => {
    setProcessing(true);
    FlashesStore.reset();

    PropertySettingsUpdateMutation(
      propertyUuid,
      input,
      handlePropertySettingsUpdateSuccess,
      handlePropertySettingsUpdateFailure,
    );
  };

  const addr = getFullAddressString(address);
  const pageTitle = intl.formatMessage(
    { id: 'property.property_settings.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' },
    { propertyTitle: title },
  );

  const breadcrumbProperties = intl.formatMessage({ id: 'property.property_settings.breadcrumbs.properties.label', defaultMessage: ' Properties' });
  const breadcrumbSettings = intl.formatMessage({ id: 'property.property_settings.breadcrumbs.settings.label', defaultMessage: 'Settings' });
  const headingContext = intl.formatMessage({ id: 'property.property_settings.title', defaultMessage: 'Property Settings' });

  const generalSettingsTitle = intl.formatMessage({ id: 'property.property_settings.general_settings_title', defaultMessage: 'General Settings' });

  const activeTariffsTitle = intl.formatMessage({ id: 'property.property_settings.tariffs.active_tariffs.title', defaultMessage: 'Active tariffs by meter' });
  const allTariffsTitle = intl.formatMessage({ id: 'property.property_settings.tariffs.all_tariffs.title', defaultMessage: 'All tariffs' });

  const usersTitle = intl.formatMessage({ id: 'property.property_settings.users.title', defaultMessage: 'Users' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs
        breadcrumbs={[
          {
            name: breadcrumbProperties, path: '/properties',
          },
          { name: title, path: `/properties/${uuid}` },
          { name: breadcrumbSettings },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={headingContext}
      />
      <h2>{generalSettingsTitle}</h2>
      <p>
        <FormattedMessage
          id="property.property_settings.general_settings_description"
          defaultMessage="Here you can edit the title of the property."
          values={{
            link: (chunks) => (
              <LinkInI18nBlock
                url={intl.formatMessage({ id: 'property.property_settings.general_settings_description.url', defaultMessage: 'property.property_settings.general_settings_description.url' })}
                urlKey="property.property_settings.general_settings_description.url"
                chunks={chunks}
              />
            ),
          }}
        />
      </p>
      <PropertySettingsForm
        property={propertyData}
        handlePropertySettingsUpdateFunc={handlePropertySettingsUpdate}
        processing={processing}
      />
      <div className="d-flex flex-wrap">
        <h2 id="tariffs">{activeTariffsTitle}</h2>
        <div className="ms-auto">
          <Link to={`/properties/${uuid}/tariffs`} className="btn btn-darken property-all-tariffs">
            {allTariffsTitle}
          </Link>
        </div>
      </div>
      <p>
        <FormattedMessage
          id="property.property_settings.tariffs.disclaimer"
          defaultMessage="Your electricity tariffs are listed here by meter and are GST exclusive."
          values={{
            link: (chunks) => (
              <LinkInI18nBlock
                url={intl.formatMessage({ id: 'property.property_settings.tariffs.disclaimer.url', defaultMessage: 'property.property_settings.tariffs.disclaimer.url' })}
                urlKey="property.property_settings.tariffs.disclaimer.url"
                chunks={chunks}
              />
            ),
          }}
        />
      </p>
      <PropertySettingsMeterTariff property={propertyData} />
      <hr />
      <h2>{usersTitle}</h2>
      <p>
        <FormattedMessage
          id="property.property_settings.users.description"
          defaultMessage="These are the users who can manage this property."
          values={{
            link: (chunks) => (
              <LinkInI18nBlock
                url={intl.formatMessage({ id: 'property.property_settings.users.description.url', defaultMessage: 'property.property_settings.users.description.url' })}
                urlKey="property.property_settings.users.description.url"
                chunks={chunks}
              />
            ),
          }}
        />
      </p>
      <PropertySettingsUsers property={propertyData} />
    </>
  );
}

PropertySettings.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default PropertySettings;
